import React, { useState } from 'react';
import { Truck, ChevronDown, ChevronRight, Play, Pause, MapPin, Package, Loader2, Search, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DriversList({ drivers, deliveryPoints, orders, activeView, onDeliverySequenceUpdated }) {
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [showingDeliveries, setShowingDeliveries] = useState(true);
  const [loadingStates, setLoadingStates] = useState({}); // Track loading per driver and action
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('with-assignments'); // Default: show drivers with deliveries or orders
  const [sequenceLoadingId, setSequenceLoadingId] = useState(null);

  const toggleDriver = (driverId) => {
    setExpandedDriver(expandedDriver === driverId ? null : driverId);
  };

  const setLoading = (driverId, action, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [`${driverId}-${action}`]: isLoading
    }));
  };

  const isLoading = (driverId, action) => {
    return loadingStates[`${driverId}-${action}`] || false;
  };

  const sortBySequence = (a, b) => {
    if (a.sequence && b.sequence) return a.sequence - b.sequence;
    if (a.sequence) return -1;
    if (b.sequence) return 1;
    return a.id - b.id;
  };

  const getDriverDeliveries = (driverId) => {
    return deliveryPoints
      .filter(dp => dp.driver_id === driverId)
      .sort(sortBySequence);
  };

  const getDriverOrders = (driverId) => {
    return orders
      .filter(order => order.driver_id === driverId)
      .sort(sortBySequence);
  };

  // Filter logic
  const getFilteredDrivers = () => {
    let filtered = drivers;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(driver => {
        const name = (driver.name || '').toLowerCase();
        const id = driver.id.toString();
        return name.includes(search) || id.includes(search);
      });
    }

    // Apply chip filter
    filtered = filtered.filter(driver => {
      const driverDeliveries = getDriverDeliveries(driver.id);
      const driverOrders = getDriverOrders(driver.id);
      const hasDeliveries = driverDeliveries.length > 0;
      const hasOrders = driverOrders.length > 0;
      const hasPendingDeliveries = driverDeliveries.some(dp => dp.status === 'pending');
      const hasPendingOrders = driverOrders.some(order => order.status === 'pending');
      const hasEnCaminoDeliveries = driverDeliveries.some(dp => dp.status === 'encamino');
      const hasEnCaminoOrders = driverOrders.some(order => order.status === 'encamino');

      switch (activeFilter) {
        case 'all':
          return true;
        case 'with-deliveries':
          return hasDeliveries;
        case 'with-orders':
          return hasOrders;
        case 'with-assignments':
          return hasDeliveries || hasOrders;
        case 'idle':
          return !hasDeliveries && !hasOrders;
        case 'with-pending':
          return hasPendingDeliveries || hasPendingOrders;
        case 'en-route':
          return hasEnCaminoDeliveries || hasEnCaminoOrders;
        default:
          return true;
      }
    });

    return filtered;
  };

  // Calculate counts for each filter
  const getFilterCounts = () => {
    const counts = {
      all: drivers.length,
      'with-deliveries': 0,
      'with-orders': 0,
      'with-assignments': 0,
      'idle': 0,
      'with-pending': 0,
      'en-route': 0,
    };

    drivers.forEach(driver => {
      const driverDeliveries = getDriverDeliveries(driver.id);
      const driverOrders = getDriverOrders(driver.id);
      const hasDeliveries = driverDeliveries.length > 0;
      const hasOrders = driverOrders.length > 0;
      const hasPendingDeliveries = driverDeliveries.some(dp => dp.status === 'pending');
      const hasPendingOrders = driverOrders.some(order => order.status === 'pending');
      const hasEnCaminoDeliveries = driverDeliveries.some(dp => dp.status === 'encamino');
      const hasEnCaminoOrders = driverOrders.some(order => order.status === 'encamino');

      if (hasDeliveries) counts['with-deliveries']++;
      if (hasOrders) counts['with-orders']++;
      if (hasDeliveries || hasOrders) counts['with-assignments']++;
      if (!hasDeliveries && !hasOrders) counts['idle']++;
      if (hasPendingDeliveries || hasPendingOrders) counts['with-pending']++;
      if (hasEnCaminoDeliveries || hasEnCaminoOrders) counts['en-route']++;
    });

    return counts;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      encamino: 'bg-blue-100 text-blue-800',
      entregado: 'bg-green-100 text-green-800',
      cancelado: 'bg-gray-100 text-gray-800',
      rechazado: 'bg-red-100 text-red-800',
      postergado: 'bg-orange-100 text-orange-800',
      completado: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const handleStartRoute = async (driverId) => {
    setLoading(driverId, 'start', true);
    
    try {
      const token = localStorage.getItem('token');
      const driver = drivers.find(d => d.id === driverId);
      
      if (!driver) {
        alert('Conductor no encontrado');
        return;
      }

      if (showingDeliveries) {
        // Handle delivery routes
        const driverDeliveries = getDriverDeliveries(driverId)
          .filter(dp => dp.status === 'pending' && dp.latitude && dp.longitude);

        if (driverDeliveries.length === 0) {
          alert('No hay entregas pendientes con ubicación para este conductor');
          return;
        }

        const startPoint = {
          lat: driver.latitude,
          lng: driver.longitude,
        };

        const endPoint = {
          lat: driver.home_latitude || driver.latitude,
          lng: driver.home_longitude || driver.longitude,
        };

        const waypoints = driverDeliveries.map(dp => ({
          lat: dp.latitude,
          lng: dp.longitude,
        }));

        const deliveryIds = driverDeliveries.map(dp => dp.id);

        await axios.post(
          `${API_URL}/api/create_route_deliveries`,
          {
            startPoint: `${startPoint.lat},${startPoint.lng}`,
            endPoint: `${endPoint.lat},${endPoint.lng}`,
            waypoints: waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|'),
            deliveryIds: deliveryIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        alert('Ruta de entregas creada exitosamente');
      } else {
        // Handle pickup routes
        const driverOrders = getDriverOrders(driverId)
          .filter(order => order.status === 'pending' && order.address?.latitude && order.address?.longitude);

        if (driverOrders.length === 0) {
          alert('No hay recojos pendientes con ubicación para este conductor');
          return;
        }

        const startPoint = {
          lat: driver.latitude,
          lng: driver.longitude,
        };

        const endPoint = {
          lat: -12.00093305507352,
          lng: -77.05374889286432,
        };

        const waypoints = driverOrders.map(order => ({
          lat: order.address.latitude,
          lng: order.address.longitude,
        }));

        const orderIds = driverOrders.map(order => order.id);

        await axios.post(
          `${API_URL}/api/create_route`,
          {
            startPoint: `${startPoint.lat},${startPoint.lng}`,
            endPoint: `${endPoint.lat},${endPoint.lng}`,
            waypoints: waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|'),
            orderIds: orderIds,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        alert('Ruta de recojos creada exitosamente');
      }
    } catch (error) {
      console.error('Error creating route:', error);
      alert('Error al crear la ruta');
    } finally {
      setLoading(driverId, 'start', false);
    }
  };

  const handlePauseRoute = async (driverId) => {
    setLoading(driverId, 'pause', true);
    
    try {
      const encaminoItems = showingDeliveries
        ? getDriverDeliveries(driverId).filter(dp => dp.status === 'encamino')
        : getDriverOrders(driverId).filter(order => order.status === 'encamino');

      const ids = encaminoItems.map(item => item.id);

      if (ids.length === 0) {
        alert('No hay entregas/recojos en camino para pausar');
        return;
      }

      const endpoint = showingDeliveries
        ? '/api/update_delivery_status'
        : '/api/update_order_status';

      await axios.put(`${API_URL}${endpoint}`, {
        ids: ids,
        status: 'pending'
      });

      alert('Ruta pausada exitosamente');
    } catch (error) {
      console.error('Error pausing route:', error);
      alert('Error al pausar la ruta');
    } finally {
      setLoading(driverId, 'pause', false);
    }
  };

  const handleResumeRoute = async (driverId) => {
    setLoading(driverId, 'resume', true);
    
    try {
      const pendingItems = showingDeliveries
        ? getDriverDeliveries(driverId).filter(dp => dp.status === 'pending')
        : getDriverOrders(driverId).filter(order => order.status === 'pending');

      const ids = pendingItems.map(item => item.id);

      if (ids.length === 0) {
        alert('No hay entregas/recojos pendientes para iniciar');
        return;
      }

      const endpoint = showingDeliveries
        ? '/api/update_delivery_status'
        : '/api/update_order_status';

      await axios.put(`${API_URL}${endpoint}`, {
        ids: ids,
        status: 'encamino'
      });

      alert('Ruta reanudada exitosamente');
    } catch (error) {
      console.error('Error resuming route:', error);
      alert('Error al reanudar la ruta');
    } finally {
      setLoading(driverId, 'resume', false);
    }
  };

  const handleDeliverySequenceChange = async (deliveryPointId, sequence) => {
    const nextSequence = parseInt(sequence, 10);

    if (!deliveryPointId || Number.isNaN(nextSequence) || nextSequence < 1) {
      return;
    }

    setSequenceLoadingId(deliveryPointId);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/control_tower/delivery_points/${deliveryPointId}/sequence`,
        { sequence: nextSequence },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (onDeliverySequenceUpdated) {
        await onDeliverySequenceUpdated();
      }
    } catch (error) {
      console.error('Error updating delivery sequence:', error);
      alert('Error al actualizar la secuencia');
    } finally {
      setSequenceLoadingId(null);
    }
  };

  const truncateName = (name, maxLength = 12) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  const filteredDrivers = getFilteredDrivers();
  const filterCounts = getFilterCounts();

  const filters = [
    { id: 'with-assignments', label: 'Con Asignación', count: filterCounts['with-assignments'] },
    { id: 'all', label: 'Todos', count: filterCounts.all },
    { id: 'with-deliveries', label: 'Con Entregas', count: filterCounts['with-deliveries'] },
    { id: 'with-orders', label: 'Con Recojos', count: filterCounts['with-orders'] },
    { id: 'idle', label: 'Sin Asignación', count: filterCounts.idle },
    { id: 'with-pending', label: 'Con Pendientes', count: filterCounts['with-pending'] },
    { id: 'en-route', label: 'En Ruta', count: filterCounts['en-route'] },
  ];

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-3 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800 font-montserrat">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-100">
            <Truck className="w-4 h-4 text-blue-700" />
          </span>
          Conductores ({filteredDrivers.length})
        </h2>
      </div>

      {/* Search Bar */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre o ID..."
            className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-9 text-sm focus:ring-2 focus:ring-[#B1336E] focus:border-transparent font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Chips */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors font-montserrat ${
                activeFilter === filter.id
                  ? 'bg-[#B1336E] text-white'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredDrivers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Truck className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              {searchTerm ? 'No se encontraron conductores' : 'No hay conductores que coincidan con los filtros'}
            </p>
            {(searchTerm || activeFilter !== 'with-assignments') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('with-assignments');
                }}
                className="mt-3 text-xs text-[#B1336E] hover:text-[#B1336E]/80 font-medium font-montserrat"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {filteredDrivers.map((driver) => {
              const driverDeliveries = getDriverDeliveries(driver.id);
              const driverOrders = getDriverOrders(driver.id);
              const isExpanded = expandedDriver === driver.id;

              return (
                <div
                  key={driver.id}
                  className="overflow-hidden rounded-md border border-slate-200 bg-white"
                >
                  {/* Driver Header */}
                  <button
                    onClick={() => toggleDriver(driver.id)}
                    className="flex w-full items-center justify-between gap-2 p-3 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Truck className="h-4 w-4 flex-shrink-0 text-[#B1336E]" />
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-bold text-slate-800 font-montserrat">
                          {driver.name || `Conductor ${driver.id}`}
                        </p>
                        <p className="text-xs text-slate-500 font-montserrat">
                          Entregas: {driverDeliveries.length} | Recojos: {driverOrders.length}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-200">
                      {/* Toggle Buttons */}
                      <div className="flex gap-1 bg-slate-50 p-2">
                        <button
                          onClick={() => setShowingDeliveries(true)}
                          className={`flex-1 rounded px-2 py-1.5 text-xs transition-colors font-montserrat ${
                            showingDeliveries
                              ? 'bg-[#B1336E] text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <MapPin className="w-3 h-3 inline mr-1" />
                          Entregas
                        </button>
                        <button
                          onClick={() => setShowingDeliveries(false)}
                          className={`flex-1 rounded px-2 py-1.5 text-xs transition-colors font-montserrat ${
                            !showingDeliveries
                              ? 'bg-[#B1336E] text-white'
                              : 'bg-white text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Package className="w-3 h-3 inline mr-1" />
                          Recojos
                        </button>
                      </div>

                      {/* Route Controls */}
                      <div className="grid grid-cols-3 gap-1 border-t border-slate-200 bg-slate-50 p-2">
                        <button
                          onClick={() => handleStartRoute(driver.id)}
                          disabled={isLoading(driver.id, 'start')}
                          className="flex items-center justify-center gap-1 rounded bg-[#67A43C] px-2 py-1.5 text-xs text-white transition-colors hover:bg-[#67A43C]/80 disabled:cursor-not-allowed disabled:bg-slate-300 font-montserrat"
                        >
                          {isLoading(driver.id, 'start') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'start') ? 'Creando...' : 'Crear Ruta'}
                        </button>
                        <button
                          onClick={() => handlePauseRoute(driver.id)}
                          disabled={isLoading(driver.id, 'pause')}
                          className="flex items-center justify-center gap-1 rounded bg-orange-500 px-2 py-1.5 text-xs text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-slate-300 font-montserrat"
                        >
                          {isLoading(driver.id, 'pause') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Pause className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'pause') ? 'Pausando...' : 'Pausar'}
                        </button>
                        <button
                          onClick={() => handleResumeRoute(driver.id)}
                          disabled={isLoading(driver.id, 'resume')}
                          className="flex items-center justify-center gap-1 rounded bg-blue-500 px-2 py-1.5 text-xs text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-300 font-montserrat"
                        >
                          {isLoading(driver.id, 'resume') ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          {isLoading(driver.id, 'resume') ? 'Reanudando...' : 'Reanudar'}
                        </button>
                      </div>

                      {/* Items List */}
                      <div className="max-h-64 space-y-1 overflow-y-auto p-2">
                        {showingDeliveries ? (
                          driverDeliveries.length > 0 ? (
                            driverDeliveries.map((dp) => (
                              <div
                                key={dp.id}
                                className="bg-slate-50 rounded p-2 text-xs"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-[#B1336E] font-montserrat">
                                    #{dp.id}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded font-montserrat ${getStatusColor(
                                      dp.status
                                    )}`}
                                  >
                                    {dp.status}
                                  </span>
                                </div>
                                <p className="text-slate-700 font-medium font-montserrat">
                                  {truncateName(dp.recipient_name)}
                                </p>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  <label
                                    htmlFor={`delivery-sequence-${dp.id}`}
                                    className="text-[10px] font-semibold text-slate-500 font-montserrat"
                                  >
                                    Secuencia
                                  </label>
                                  <div className="flex items-center gap-1">
                                    {sequenceLoadingId === dp.id && (
                                      <Loader2 className="h-3 w-3 animate-spin text-[#B1336E]" />
                                    )}
                                    <select
                                      id={`delivery-sequence-${dp.id}`}
                                      value={dp.sequence || ''}
                                      onChange={(event) =>
                                        handleDeliverySequenceChange(dp.id, event.target.value)
                                      }
                                      disabled={sequenceLoadingId !== null}
                                      className="h-7 rounded border border-slate-300 bg-white px-2 text-xs font-bold text-slate-700 focus:border-transparent focus:ring-2 focus:ring-[#B1336E] disabled:cursor-not-allowed disabled:bg-slate-100 font-montserrat"
                                    >
                                      {!dp.sequence && <option value="">-</option>}
                                      {Array.from({ length: driverDeliveries.length }, (_, index) => {
                                        const sequence = index + 1;
                                        return (
                                          <option key={sequence} value={sequence}>
                                            {sequence}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-500 text-center py-4 font-montserrat">
                              Sin entregas asignadas
                            </p>
                          )
                        ) : driverOrders.length > 0 ? (
                          driverOrders.map((order) => (
                            <div
                              key={order.id}
                              className="bg-slate-50 rounded p-2 text-xs"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-[#B1336E] font-montserrat">
                                  #{order.id}
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded font-montserrat ${getStatusColor(
                                    order.status
                                  )}`}
                                >
                                  {order.status}
                                </span>
                              </div>
                              <p className="text-slate-700 font-medium font-montserrat">
                                {truncateName(order.customer_name)}
                              </p>
                              {order.sequence && (
                                <p className="text-slate-500 text-[10px] font-montserrat">
                                  Secuencia: {order.sequence}
                                </p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-500 text-center py-4 font-montserrat">
                            Sin recojos asignados
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DriversList;
